import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { GiftIcon, PlusIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_LOYALTY_PROGRAMS } from '../../graphql/loyalty-queries'
import {
  CLONE_LOYALTY_PROGRAM,
  DELETE_LOYALTY_PROGRAM_DRAFT,
} from '../../graphql/loyalty-mutations'
import type { LoyaltyProgram } from '../../types/loyalty'
import { formatEuros, parseApiDate } from '../../lib/format'
import LoadingSpinner from '../../components/LoadingSpinner'
import { NewDraftDialog, PublishDialog, RenameDialog, SetEndDialog } from './programDialogs'
import { StatusPill } from './shared'

// P04 — the programme list. Programmes are sequential: exactly one is current
// at a time, a published one is frozen, and changing the rules means cloning
// it into a new draft.

/** ISO timestamp → "20 sep 2026"; "—" when there is none. */
const formatDay = (value: string | null): string =>
  parseApiDate(value)?.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }) ?? '—'

type Dialog =
  | { kind: 'new' }
  | { kind: 'publish'; program: LoyaltyProgram }
  | { kind: 'rename'; program: LoyaltyProgram }
  | { kind: 'end'; program: LoyaltyProgram }

const LoyaltyProgramsPage = () => {
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<Dialog | null>(null)

  const { data, loading, error, refetch } = useQuery(GET_LOYALTY_PROGRAMS, {
    fetchPolicy: 'cache-and-network',
  })
  const programs: LoyaltyProgram[] = data?.loyaltyPrograms ?? []
  const currentProgram = programs.find((program) => program.is_current) ?? null

  const [cloneProgram] = useMutation(CLONE_LOYALTY_PROGRAM)
  const [deleteDraft] = useMutation(DELETE_LOYALTY_PROGRAM_DRAFT)

  // Mutation failures are toasted by the global errorLink in src/lib/apollo.ts;
  // adding a toast.error here would double them.
  const handleClone = async (program: LoyaltyProgram) => {
    try {
      const result = await cloneProgram({ variables: { id: program.id } })
      const clone: LoyaltyProgram | undefined = result.data?.cloneLoyaltyProgram
      void refetch()
      if (!clone) return
      toast.success('Draft created from the clone')
      navigate(`/loyalty/programs/${clone.id}`)
    } catch {
      // errorLink already toasted.
    }
  }

  const handleDelete = async (program: LoyaltyProgram) => {
    if (!window.confirm(`Delete draft “${program.name}”? Its rungs go with it.`)) return
    try {
      await deleteDraft({ variables: { id: program.id } })
      toast.success('Draft deleted')
      void refetch()
    } catch {
      // errorLink already toasted.
    }
  }

  /** The muted second line under the name: what this programme is, in two words. */
  const subLine = (program: LoyaltyProgram): string | null => {
    const parts: string[] = []
    if (program.is_current) parts.push('current')
    const clonedFrom = program.cloned_from_id
      ? programs.find((other) => other.id === program.cloned_from_id)
      : undefined
    if (clonedFrom) parts.push(`cloned from ${clonedFrom.name}`)
    return parts.length > 0 ? parts.join(' · ') : null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Curry Me Club</h1>
          <p className="mt-1 text-sm text-gray-500">
            Programs are sequential: one is current at a time. A published program is frozen —
            clone it to change the rules.
          </p>
        </div>
        <button
          onClick={() => setDialog({ kind: 'new' })}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          New draft
        </button>
      </div>

      {/* Content */}
      {loading && programs.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error && programs.length === 0 ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : programs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
          <GiftIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-semibold text-gray-900">No programs yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            A program holds the card size, what earns a stamp and the reward ladder.
          </p>
          <button
            onClick={() => setDialog({ kind: 'new' })}
            className="btn-primary mt-6 inline-flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            New draft
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Starts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ends
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Card
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {programs.map((program) => {
                const sub = subLine(program)
                return (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">
                      <div className="font-medium text-gray-900">{program.name}</div>
                      {sub && <div className="text-xs text-gray-500">{sub}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusPill status={program.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDay(program.starts_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {program.ends_at
                        ? formatDay(program.ends_at)
                        : program.status === 'PUBLISHED'
                          ? 'open'
                          : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {program.card_size} · {formatEuros(program.min_order_cents)} min
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      {program.status === 'DRAFT' && (
                        <>
                          <Link
                            to={`/loyalty/programs/${program.id}`}
                            className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                          >
                            <PencilIcon className="h-4 w-4" />
                            Edit
                          </Link>
                          <button
                            onClick={() => setDialog({ kind: 'publish', program })}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Publish…
                          </button>
                          <button
                            onClick={() => void handleDelete(program)}
                            className="text-red-600 hover:text-red-900 inline-flex items-center gap-1"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </>
                      )}
                      {program.status === 'PUBLISHED' && (
                        <>
                          <Link
                            to={`/loyalty/programs/${program.id}`}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => setDialog({ kind: 'rename', program })}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Rename…
                          </button>
                          <button
                            onClick={() => setDialog({ kind: 'end', program })}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Set end…
                          </button>
                          <button
                            onClick={() => void handleClone(program)}
                            className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                            Clone
                          </button>
                        </>
                      )}
                      {program.status === 'ENDED' && (
                        <>
                          <Link
                            to={`/loyalty/programs/${program.id}`}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => void handleClone(program)}
                            className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                            Clone
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === 'new' && <NewDraftDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'publish' && (
        <PublishDialog
          program={dialog.program}
          currentProgram={currentProgram}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'rename' && (
        <RenameDialog program={dialog.program} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'end' && (
        <SetEndDialog program={dialog.program} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}

export default LoyaltyProgramsPage
